import {apiGetRequest, apiPostRequest, backendServicePrefix} from '../../../utils/apiHelpers';
import {
  createJourneyActivity,
  sendRenewalClickCode,
  verifyRenewalClickCode,
} from '../../../utils/apiHandlers';
import {mockPageModel, mockAuthPageModel} from './renewalClick.mock';
import {RENEWAL_JOURNEY_NAME, RenewalJourneyEvent} from '../constants/renewalClick';

const MOCK_ENABLED = process.env.REACT_APP_NODE_ENV === 'local' && process.env.REACT_APP_MOCK_RENEWAL !== 'false';
const MOCK_AUTH_ENABLED = MOCK_ENABLED && process.env.REACT_APP_MOCK_RENEWAL_AUTH === 'true';

let mockAuthCompleted = false;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const renewalUrl = (shortLinkKey, path = '') =>
  `auto/renewal/${encodeURIComponent(shortLinkKey)}${path}`;

// The auth endpoints answer 200 with {success:false, message} — surface that message.
const unwrap = (response) => {
  if (response?.success === false) {
    throw new Error(response.message || 'REQUEST_FAILED');
  }

  return response?.data ?? response;
};

export const fetchRenewalPageModel = async (shortLinkKey) => {
  if (MOCK_ENABLED) {
    await delay(800);
    if (MOCK_AUTH_ENABLED && !mockAuthCompleted) {
      return mockAuthPageModel;
    }
    return mockPageModel;
  }
  const response = await apiGetRequest(renewalUrl(shortLinkKey));
  return response?.data ?? response;
};

export const sendRenewalCode = async (shortLinkKey, method) => {
  if (MOCK_ENABLED) {
    await delay(500);
    return {status: 'SENT'};
  }
  return unwrap(await sendRenewalClickCode(shortLinkKey, method));
};

export const verifyRenewalCode = async (shortLinkKey, code) => {
  if (MOCK_ENABLED) {
    await delay(800);
    mockAuthCompleted = true;
    return {authStatus: 'AUTHENTICATED'};
  }
  return unwrap(await verifyRenewalClickCode(shortLinkKey, code));
};

export const submitRenewalPayment = async (shortLinkKey, paymentData) => {
  if (MOCK_ENABLED) {
    await delay(1500);
    return {isSuccess: true};
  }
  const response = await apiPostRequest(renewalUrl(shortLinkKey, '/payment'), paymentData);
  return response?.data ?? response;
};

export const requestSigningSession = async (shortLinkKey) => {
  if (MOCK_ENABLED) {
    await delay(500);
    return {signingUrl: 'https://demo.docusign.net/Signing/mock'};
  }
  const response = await apiPostRequest(renewalUrl(shortLinkKey, '/signing-session'), {});
  return unwrap(response);
};

export const getSigningProvider = async (shortLinkKey) => {
  if (MOCK_ENABLED) return {provider: 'DocuSign'};
  return unwrap(await apiGetRequest(renewalUrl(shortLinkKey, '/signing-provider')));
};

export const getSigningStatus = async (shortLinkKey) => {
  if (MOCK_ENABLED) {
    return {signed: false};
  }
  const response = await apiGetRequest(renewalUrl(shortLinkKey, '/signing-status'));
  return unwrap(response);
};

export const getSigningEventsUrl = (shortLinkKey) =>
  `${backendServicePrefix}/${renewalUrl(shortLinkKey, '/events')}`;

export const fireRenewalJourneyActivity = (opportunityId, eventName, extra = {}) =>
  createJourneyActivity({
    eventName,
    extJourneyName: RENEWAL_JOURNEY_NAME,
    opportunityId,
    ...extra,
  });

export const handleContactClick = async (opportunityId, screen, channel) => {
  fireRenewalJourneyActivity(opportunityId, RenewalJourneyEvent.CONTACT_CLICKED, {location: screen});
  try {
    unwrap(await apiPostRequest('auto/renewal/contact-clicked', {opportunityId, screen, channel}));
    return true;
  } catch {
    return false;
  }
};
